import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  TruckIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  PhoneIcon,
  EnvelopeIcon,
  EyeIcon,
  ShareIcon,
  BellIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  PlayIcon,
  PauseIcon
} from '@heroicons/react/24/outline'

// Interfaces
interface EventoSeguimiento {
  id: string
  fecha: string
  estado: string
  descripcion: string
  ubicacion?: string
  coordenadas?: {
    lat: number
    lng: number
  }
  responsable?: string
  observaciones?: string
  tipo: 'info' | 'success' | 'warning' | 'error'
  icono?: string
}

interface UbicacionActual {
  lat: number
  lng: number
  direccion: string
  timestamp: string
  velocidad?: number
  rumbo?: string
}

interface Envio {
  id: string
  numeroGuia: string
  estado: 'pendiente' | 'en_transito' | 'entregado' | 'cancelado' | 'devuelto'
  fechaCreacion: string
  fechaEntregaEstimada: string
  fechaEntregaReal?: string
  remitente: {
    nombre: string
    direccion: string
    ciudad: string
  }
  destinatario: {
    nombre: string
    direccion: string
    ciudad: string
  }
  conductor?: {
    id: string
    nombre: string
    telefono: string
    email?: string
  }
  vehiculo?: {
    id: string
    placa: string
    tipo: string
  }
  seguimiento: EventoSeguimiento[]
  ubicacionActual?: UbicacionActual
  tiempoEstimadoLlegada?: string
  distanciaRestante?: number
}

const PaginaSeguimientoEnvio: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [envio, setEnvio] = useState<Envio | null>(null)
  const [cargando, setCargando] = useState(true)
  const [actualizandoUbicacion, setActualizandoUbicacion] = useState(false)
  const [mostrarMapa, setMostrarMapa] = useState(true)
  const [seguimientoEnVivo, setSeguimientoEnVivo] = useState(false)
  const [notificacionesActivas, setNotificacionesActivas] = useState(true)

  // Cargar datos del envío
  useEffect(() => {
    const cargarEnvio = async () => {
      setCargando(true)
      
      // Simular carga de datos
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Datos de ejemplo
      const envioEjemplo: Envio = {
        id: id || '1',
        numeroGuia: 'LXP-2024-001',
        estado: 'en_transito',
        fechaCreacion: '2024-01-15T10:30:00Z',
        fechaEntregaEstimada: '2024-01-17T16:00:00Z',
        remitente: {
          nombre: 'Juan Pérez',
          direccion: 'Calle 123 #45-67, Chapinero',
          ciudad: 'Bogotá'
        },
        destinatario: {
          nombre: 'María García',
          direccion: 'Carrera 89 #12-34, El Poblado',
          ciudad: 'Medellín'
        },
        conductor: {
          id: 'c1',
          nombre: 'Carlos Rodríguez',
          telefono: '+57 302 345 6789',
          email: 'carlos.rodriguez@logixport.com'
        },
        vehiculo: {
          id: 'v1',
          placa: 'ABC-123',
          tipo: 'Motocicleta'
        },
        ubicacionActual: {
          lat: 6.2442,
          lng: -75.5812,
          direccion: 'Autopista Norte, Km 45, Barbosa, Antioquia',
          timestamp: '2024-01-16T14:30:00Z',
          velocidad: 65,
          rumbo: 'Sur'
        },
        tiempoEstimadoLlegada: '2024-01-16T16:30:00Z',
        distanciaRestante: 45,
        seguimiento: [
          {
            id: '1',
            fecha: '2024-01-15T10:30:00Z',
            estado: 'creado',
            descripcion: 'Envío creado y registrado en el sistema',
            ubicacion: 'Centro de distribución Bogotá',
            coordenadas: { lat: 4.7110, lng: -74.0721 },
            responsable: 'Sistema LogiXPort',
            tipo: 'info',
            icono: 'document'
          },
          {
            id: '2',
            fecha: '2024-01-15T14:15:00Z',
            estado: 'recolectado',
            descripcion: 'Paquete recolectado del remitente',
            ubicacion: 'Calle 123 #45-67, Chapinero, Bogotá',
            coordenadas: { lat: 4.6097, lng: -74.0817 },
            responsable: 'Carlos Rodríguez',
            observaciones: 'Recolección exitosa, paquete en perfecto estado',
            tipo: 'success',
            icono: 'check'
          },
          {
            id: '3',
            fecha: '2024-01-15T16:45:00Z',
            estado: 'en_centro',
            descripcion: 'Paquete llegó al centro de distribución',
            ubicacion: 'Centro de distribución Bogotá',
            coordenadas: { lat: 4.7110, lng: -74.0721 },
            responsable: 'Ana López',
            tipo: 'info',
            icono: 'building'
          },
          {
            id: '4',
            fecha: '2024-01-16T08:30:00Z',
            estado: 'despachado',
            descripcion: 'Paquete despachado hacia destino',
            ubicacion: 'Centro de distribución Bogotá',
            coordenadas: { lat: 4.7110, lng: -74.0721 },
            responsable: 'Carlos Rodríguez',
            observaciones: 'Salida programada hacia Medellín',
            tipo: 'info',
            icono: 'truck'
          },
          {
            id: '5',
            fecha: '2024-01-16T12:15:00Z',
            estado: 'en_transito',
            descripcion: 'Paquete en ruta hacia destino',
            ubicacion: 'Peaje Girardota, Antioquia',
            coordenadas: { lat: 6.3797, lng: -75.4486 },
            responsable: 'Carlos Rodríguez',
            observaciones: 'Viaje sin contratiempos, llegada estimada 4:30 PM',
            tipo: 'success',
            icono: 'location'
          },
          {
            id: '6',
            fecha: '2024-01-16T14:30:00Z',
            estado: 'en_transito',
            descripcion: 'Actualización de ubicación',
            ubicacion: 'Autopista Norte, Km 45, Barbosa, Antioquia',
            coordenadas: { lat: 6.2442, lng: -75.5812 },
            responsable: 'Sistema GPS',
            observaciones: 'Velocidad: 65 km/h, Rumbo: Sur',
            tipo: 'info',
            icono: 'refresh'
          }
        ]
      }
      
      setEnvio(envioEjemplo)
      setCargando(false)
    }

    cargarEnvio()
  }, [id])

  // Actualizar ubicación en tiempo real
  useEffect(() => {
    if (!seguimientoEnVivo || !envio) return

    const interval = setInterval(async () => {
      setActualizandoUbicacion(true)
      
      // Simular actualización de ubicación
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Actualizar ubicación (simulado)
      setEnvio(prev => {
        if (!prev) return prev
        
        const nuevaUbicacion: UbicacionActual = {
          lat: prev.ubicacionActual!.lat + (Math.random() - 0.5) * 0.01,
          lng: prev.ubicacionActual!.lng + (Math.random() - 0.5) * 0.01,
          direccion: 'Autopista Norte, Km 43, Barbosa, Antioquia',
          timestamp: new Date().toISOString(),
          velocidad: 60 + Math.random() * 20,
          rumbo: 'Sur'
        }
        
        return {
          ...prev,
          ubicacionActual: nuevaUbicacion,
          distanciaRestante: Math.max(0, (prev.distanciaRestante || 45) - 2)
        }
      })
      
      setActualizandoUbicacion(false)
    }, 30000) // Actualizar cada 30 segundos

    return () => clearInterval(interval)
  }, [seguimientoEnVivo, envio])

  // Obtener color del estado
  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'en_transito':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'entregado':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'cancelado':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'devuelto':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  // Obtener icono del estado
  const obtenerIconoEstado = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return ClockIcon
      case 'en_transito':
        return TruckIcon
      case 'entregado':
        return CheckCircleIcon
      case 'cancelado':
        return XCircleIcon
      case 'devuelto':
        return ArrowPathIcon
      default:
        return ClockIcon
    }
  }

  // Obtener icono del evento
  const obtenerIconoEvento = (icono: string, tipo: string) => {
    switch (icono) {
      case 'document':
        return DocumentTextIcon
      case 'check':
        return CheckCircleIcon
      case 'building':
        return MapPinIcon
      case 'truck':
        return TruckIcon
      case 'location':
        return MapPinIcon
      case 'refresh':
        return ArrowPathIcon
      default:
        switch (tipo) {
          case 'success':
            return CheckCircleIcon
          case 'warning':
            return ExclamationTriangleIcon
          case 'error':
            return XCircleIcon
          default:
            return InformationCircleIcon
        }
    }
  }

  // Obtener color del evento
  const obtenerColorEvento = (tipo: string) => {
    switch (tipo) {
      case 'success':
        return 'bg-green-500'
      case 'warning':
        return 'bg-yellow-500'
      case 'error':
        return 'bg-red-500'
      default:
        return 'bg-blue-500'
    }
  }

  // Formatear fecha
  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Formatear fecha corta
  const formatearFechaCorta = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Calcular tiempo restante
  const calcularTiempoRestante = (fechaEstimada: string) => {
    const ahora = new Date()
    const estimada = new Date(fechaEstimada)
    const diferencia = estimada.getTime() - ahora.getTime()
    
    if (diferencia <= 0) return 'Tiempo estimado superado'
    
    const horas = Math.floor(diferencia / (1000 * 60 * 60))
    const minutos = Math.floor((diferencia % (1000 * 60 * 60)) / (1000 * 60))
    
    if (horas > 0) {
      return `${horas}h ${minutos}m restantes`
    } else {
      return `${minutos}m restantes`
    }
  }

  // Actualizar ubicación manualmente
  const actualizarUbicacion = async () => {
    setActualizandoUbicacion(true)
    await new Promise(resolve => setTimeout(resolve, 2000))
    setActualizandoUbicacion(false)
  }

  // Compartir seguimiento
  const compartirSeguimiento = () => {
    if (navigator.share && envio) {
      navigator.share({
        title: `Seguimiento ${envio.numeroGuia}`,
        text: `Sigue tu envío ${envio.numeroGuia} en tiempo real`,
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Enlace copiado al portapapeles')
    }
  }

  if (cargando) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  if (!envio) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Envío no encontrado
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            El envío que buscas no existe o ha sido eliminado.
          </p>
          <button
            onClick={() => navigate('/dashboard/envios')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Volver a Envíos
          </button>
        </div>
      </div>
    )
  }

  const IconoEstado = obtenerIconoEstado(envio.estado)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate(`/dashboard/envios/${envio.id}`)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Volver a Detalles
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
                <MapPinIcon className="h-8 w-8 text-blue-500 mr-3" />
                Seguimiento en Vivo
              </h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className="text-xl font-semibold text-blue-600 dark:text-blue-400">
                  {envio.numeroGuia}
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${obtenerColorEstado(envio.estado)}`}>
                  <IconoEstado className="h-4 w-4 mr-1" />
                  {envio.estado.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={() => setSeguimientoEnVivo(!seguimientoEnVivo)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  seguimientoEnVivo
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {seguimientoEnVivo ? (
                  <PauseIcon className="h-4 w-4 mr-2" />
                ) : (
                  <PlayIcon className="h-4 w-4 mr-2" />
                )}
                {seguimientoEnVivo ? 'Pausar' : 'Activar'} Seguimiento
              </button>
              <button
                onClick={compartirSeguimiento}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <ShareIcon className="h-4 w-4 mr-2" />
                Compartir
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Información principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Estado actual */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <TruckIcon className="h-6 w-6 text-blue-500 mr-2" />
                  Estado Actual
                </h2>
                <button
                  onClick={actualizarUbicacion}
                  disabled={actualizandoUbicacion}
                  className="flex items-center px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg transition-colors disabled:opacity-50"
                >
                  <ArrowPathIcon className={`h-4 w-4 mr-1 ${actualizandoUbicacion ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
              </div>
              
              {envio.ubicacionActual && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                      Ubicación Actual
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-600 dark:text-gray-300">
                        📍 {envio.ubicacionActual.direccion}
                      </p>
                      <p className="text-gray-500 dark:text-gray-400">
                        Última actualización: {formatearFechaCorta(envio.ubicacionActual.timestamp)}
                      </p>
                      {envio.ubicacionActual.velocidad && (
                        <p className="text-gray-600 dark:text-gray-300">
                          🚗 Velocidad: {Math.round(envio.ubicacionActual.velocidad)} km/h
                        </p>
                      )}
                      {envio.ubicacionActual.rumbo && (
                        <p className="text-gray-600 dark:text-gray-300">
                          🧭 Rumbo: {envio.ubicacionActual.rumbo}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                      Información de Entrega
                    </h3>
                    <div className="space-y-2 text-sm">
                      {envio.tiempoEstimadoLlegada && (
                        <p className="text-gray-600 dark:text-gray-300">
                          ⏰ {calcularTiempoRestante(envio.tiempoEstimadoLlegada)}
                        </p>
                      )}
                      {envio.distanciaRestante && (
                        <p className="text-gray-600 dark:text-gray-300">
                          📏 {envio.distanciaRestante} km restantes
                        </p>
                      )}
                      <p className="text-gray-600 dark:text-gray-300">
                        🎯 Destino: {envio.destinatario.direccion}
                      </p>
                      <p className="text-gray-600 dark:text-gray-300">
                        👤 Destinatario: {envio.destinatario.nombre}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Mapa (simulado) */}
            {mostrarMapa && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                    <MapPinIcon className="h-6 w-6 text-blue-500 mr-2" />
                    Mapa de Seguimiento
                  </h2>
                  <button
                    onClick={() => setMostrarMapa(!mostrarMapa)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Placeholder del mapa */}
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg h-64 flex items-center justify-center">
                  <div className="text-center">
                    <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">
                      Mapa interactivo de seguimiento
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      (Integración con Google Maps/OpenStreetMap)
                    </p>
                  </div>
                </div>
                
                {/* Leyenda del mapa */}
                <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-300">Origen</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-300">Ubicación actual</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-300">Destino</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Timeline detallado */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                <ClockIcon className="h-6 w-6 text-blue-500 mr-2" />
                Timeline Detallado
              </h2>
              
              <div className="space-y-6">
                {envio.seguimiento.map((evento, index) => {
                  const IconoEvento = obtenerIconoEvento(evento.icono || '', evento.tipo)
                  
                  return (
                    <motion.div
                      key={evento.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="relative flex items-start space-x-4"
                    >
                      {/* Línea conectora */}
                      {index < envio.seguimiento.length - 1 && (
                        <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200 dark:bg-gray-600" />
                      )}
                      
                      {/* Icono del evento */}
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${obtenerColorEvento(evento.tipo)}`}>
                        <IconoEvento className="h-6 w-6 text-white" />
                      </div>
                      
                      {/* Contenido del evento */}
                      <div className="flex-1 min-w-0">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                              {evento.descripcion}
                            </h3>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatearFechaCorta(evento.fecha)}
                            </span>
                          </div>
                          
                          {evento.ubicacion && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 flex items-center">
                              <MapPinIcon className="h-4 w-4 mr-1" />
                              {evento.ubicacion}
                            </p>
                          )}
                          
                          {evento.responsable && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 flex items-center">
                              <UserIcon className="h-4 w-4 mr-1" />
                              {evento.responsable}
                            </p>
                          )}
                          
                          {evento.observaciones && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                              {evento.observaciones}
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Información del conductor */}
            {envio.conductor && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 text-blue-500 mr-2" />
                  Conductor Asignado
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {envio.conductor.nombre}
                    </p>
                    {envio.vehiculo && (
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {envio.vehiculo.tipo} - {envio.vehiculo.placa}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <a
                      href={`tel:${envio.conductor.telefono}`}
                      className="flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    >
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      Llamar
                    </a>
                    {envio.conductor.email && (
                      <a
                        href={`mailto:${envio.conductor.email}`}
                        className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        Email
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Configuración de notificaciones */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <BellIcon className="h-5 w-5 text-blue-500 mr-2" />
                Notificaciones
              </h3>
              
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={notificacionesActivas}
                    onChange={(e) => setNotificacionesActivas(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Notificaciones push
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Notificaciones por email
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    SMS de entrega
                  </span>
                </label>
              </div>
            </motion.div>

            {/* Información de entrega */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" />
                Información de Entrega
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Fecha estimada</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {formatearFecha(envio.fechaEntregaEstimada)}
                  </p>
                </div>
                
                {envio.tiempoEstimadoLlegada && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Tiempo restante</p>
                    <p className="font-medium text-blue-600 dark:text-blue-400">
                      {calcularTiempoRestante(envio.tiempoEstimadoLlegada)}
                    </p>
                  </div>
                )}
                
                {envio.distanciaRestante && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Distancia restante</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {envio.distanciaRestante} km
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Estado del seguimiento */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Estado del Seguimiento
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Seguimiento en vivo
                  </span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    seguimientoEnVivo
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                  }`}>
                    {seguimientoEnVivo ? 'Activo' : 'Pausado'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Última actualización
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {envio.ubicacionActual ? formatearFechaCorta(envio.ubicacionActual.timestamp) : 'N/A'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    Notificaciones
                  </span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    notificacionesActivas
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                  }`}>
                    {notificacionesActivas ? 'Habilitadas' : 'Deshabilitadas'}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PaginaSeguimientoEnvio